import type { Patient } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { Desk } from '@seen/core/models/desk'
import { isOfType } from '@seen/core/utils'
import { Button, DotSeparator, TypographyPageTitle } from '@seen/ui'
import { IconDots, IconEye, IconPencil, IconPlus } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { sortBy } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuSeparator } from '#src/components/core/dropdown-menu/DropdownMenuSeparator.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DeskComposerDialogContent } from '#src/components/DeskComposerDialogContent.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useReference } from '#src/hooks/useReference.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function ParticipantDesksPage(): JSX.Element {
  const { patientId } = useParams()
  invariant(patientId, 'Patient ID is required')
  return <ParticipantDesksPageComponent key={patientId} patientId={patientId} />
}

function ParticipantDesksPageComponent({
  patientId,
}: {
  patientId: string
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const navigateFromClickEvent = useNavigateFromClickEvent()
  const navigate = useNavigate()
  const currentPractitioner = useCurrentPractitioner()
  const participantReference = useReference<Patient>(`Patient/${patientId}`)
  const { data: unsorted, isLoading } = useSearch(
    Desk,
    Desk.queryParticipantDesks(participantReference, {
      practitioner: currentPractitioner.reference,
    })
  )
  const desks = useMemo(() => sortBy(unsorted, 'title'), [unsorted])

  const patch = usePatchMutation<Desk>().mutate

  const [deskComposerOpen, setDeskComposerOpen] = useState(false)
  const [editDesk, setEditDesk] = useState<Desk | undefined>(undefined)

  const columns: ColumnDef<Desk>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            {row.original.title}
            {row.original.access !== 'shared' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconEye className="size-3.5 text-foreground-tertiary" />
                </TooltipTrigger>
                <TooltipContent>
                  Private <DotSeparator /> Only you can see this desk
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'createdBy',
        header: 'Created by',
        cell: ({ row }) =>
          row.original.author &&
          isOfType(row.original.author, 'Practitioner') ? (
            <PractitionerDisplay practitioner={row.original.author} />
          ) : null,
      },
      {
        accessorKey: 'created',
        header: 'Created',
        cell: ({ row }) =>
          row.original.date ? <HoverDate date={row.original.date} /> : null,
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Updated',
        cell: ({ row }) =>
          row.original.lastUpdated ? (
            <HoverDate date={row.original.lastUpdated} />
          ) : null,
      },
      ...(!isReadOnly
        ? [
            {
              id: 'actions',
              cell: ({ row }: { row: { original: Desk } }) => {
                const desk = row.original
                return (
                  <div className="flex items-center justify-end gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="-my-3">
                          <IconDots className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onClick={() => {
                            setEditDesk(desk)
                            setDeskComposerOpen(true)
                          }}
                        >
                          <IconPencil className="size-4 text-foreground-secondary" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuDeleteItem
                          deleteConfirmationProps={{
                            title: `Delete "${desk.title}"?`,
                            description:
                              'Deleted desks can still be recovered by contacting our team.',
                            onConfirm: () => {
                              patch({
                                instance: desk,
                                mutateFn: (d) => {
                                  d.status = 'entered-in-error'
                                },
                              })
                            },
                          }}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )
              },
            } satisfies ColumnDef<Desk>,
          ]
        : []),
    ],
    [patch, isReadOnly]
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Desks</TypographyPageTitle>
        {!isReadOnly && (
          <Dialog open={deskComposerOpen} onOpenChange={setDeskComposerOpen}>
            <DialogButton
              type="button"
              variant="outline"
              className="flex items-center gap-2"
              aria-label="New desk"
              onClick={() => setEditDesk(undefined)}
            >
              <IconPlus className="size-4" />
              New desk…
            </DialogButton>
            <DialogDisplay>
              <DeskComposerDialogContent
                desk={editDesk}
                onCreate={(desk) => {
                  void navigate(`/Patient/${patientId}/desks/${desk.id}`)
                }}
                onUpdate={() => {
                  setDeskComposerOpen(false)
                }}
                subject={participantReference}
              />
            </DialogDisplay>
          </Dialog>
        )}
      </div>
      {isLoading && <Loader />}
      {desks && (
        <DataTable
          columns={columns}
          data={desks}
          onRowClick={(row, e) =>
            navigateFromClickEvent(`/Patient/${patientId}/desks/${row.id}`, e)
          }
          deleteRowProps={
            isReadOnly
              ? undefined
              : (desk) => ({
                  title: `Delete "${desk.title}"?`,
                  description:
                    'Deleted desks can still be recovered by contacting our team.',
                  onConfirm: () => {
                    patch({
                      instance: desk,
                      mutateFn: (d) => {
                        d.status = 'entered-in-error'
                      },
                    })
                  },
                })
          }
        />
      )}
    </div>
  )
}
